import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { PopoverType, TinyPopover } from '../../../../../components/TinyPopover';
import { DisclosureIcon } from '../../../../../components/svgs/icons/DisclosureIcon';
import { SuccessCheckIcon } from '../../../../../components/svgs/icons/SuccessCheckIcon';
import { mobileStyles } from '../styles';

interface MobileWhereToShareProps {
	connections: Api.ISocialMediaConnection[];
	selectedTargets: Api.IPostTarget[];
	onSelectionChanged: (selected: Api.IPostTarget[]) => void;
	readOnly?: boolean;
}

const MobileWhereToShare: React.FC<MobileWhereToShareProps> = observer(
	({ connections, selectedTargets, onSelectionChanged, readOnly }) => {
		const [isOpen, setIsOpen] = React.useState(false);

		const availableConnections = connections.filter(
			conn => conn.state === Api.SocialMediaConnectionState.Connected && conn.postTargetId
		);

		const getDisplayText = () => {
			if (selectedTargets.length === 0) {
				return 'Select accounts';
			} else if (selectedTargets.length === 1) {
				const target = selectedTargets[0];
				const connection = availableConnections.find(
					conn => conn.postTargetId === target.pageId && conn.type === target.provider
				);
				return connection?.postTargetDisplayName || `${target.provider}: ${target.pageId}`;
			} else {
				const firstTarget = selectedTargets[0];
				const firstConnection = availableConnections.find(
					conn => conn.postTargetId === firstTarget.pageId && conn.type === firstTarget.provider
				);
				const firstName = firstConnection?.postTargetDisplayName || `${firstTarget.provider}: ${firstTarget.pageId}`;
				return `${firstName} +${selectedTargets.length - 1} accounts`;
			}
		};

		const isTargetSelected = (connection: Api.ISocialMediaConnection) => {
			return selectedTargets.some(
				target => target.pageId === connection.postTargetId && target.provider === connection.type
			);
		};

		const toggleSelection = (connection: Api.ISocialMediaConnection) => {
			if (readOnly) return;

			const target: Api.IPostTarget = {
				pageId: connection.postTargetId,
				provider: connection.type,
				userId: connection.userId,
			};

			const isSelected = isTargetSelected(connection);
			let newTargets: Api.IPostTarget[];

			// eslint-disable-next-line unicorn/prefer-ternary
			if (isSelected) {
				newTargets = selectedTargets.filter(t => !(t.pageId === target.pageId && t.provider === target.provider));
			} else {
				newTargets = [...selectedTargets, target];
			}

			onSelectionChanged(newTargets);
		};

		const renderConnectionItem = (connection: Api.ISocialMediaConnection) => {
			const isSelected = isTargetSelected(connection);
			const platformName = connection.type.charAt(0).toUpperCase() + connection.type.slice(1);
			const displayName = connection.postTargetDisplayName || connection.userName;

			return (
				<div
					key={`${connection.type}-${connection.postTargetId}`}
					className={css(mobileStyles.whereToShareConnectionItem)}
					onClick={() => toggleSelection(connection)}
				>
					<div className={css(mobileStyles.whereToShareConnectionInfo)}>
						<div className={css(mobileStyles.whereToShareConnectionName)}>
							{platformName}: {displayName}
						</div>
						{connection.postTargetDisplayName && connection.userName && (
							<div className={css(mobileStyles.whereToShareConnectionSubtext)}>@{connection.userName}</div>
						)}
					</div>
					<div className={css(mobileStyles.whereToShareCheckboxContainer)}>
						{isSelected && <SuccessCheckIcon className={css(mobileStyles.whereToShareCheckIcon)} size={16} />}
					</div>
				</div>
			);
		};

		if (availableConnections.length === 0) {
			return (
				<div className={css(mobileStyles.whereToShareContainer)}>
					<div className={css(mobileStyles.whereToShareNoAccounts)}>No connected social media accounts found.</div>
				</div>
			);
		}

		return (
			<div className={css(mobileStyles.whereToShareContainer)}>
				<div className={css(mobileStyles.whereToShareLabel)}>Where to Share</div>
				<TinyPopover
					isOpen={isOpen}
					onRequestClose={() => setIsOpen(false)}
					anchor={
						<button
							className={css(
								mobileStyles.whereToShareSelectButton,
								readOnly && mobileStyles.whereToShareSelectButtonReadOnly
							)}
							onClick={() => !readOnly && setIsOpen(!isOpen)}
							disabled={readOnly}
						>
							<span className={css(mobileStyles.whereToShareSelectButtonText)}>{getDisplayText()}</span>
							{!readOnly && (
								<DisclosureIcon
									type='chevron'
									className={css(
										mobileStyles.whereToShareChevronIcon,
										isOpen && mobileStyles.whereToShareChevronIconOpen
									)}
								/>
							)}
						</button>
					}
					placement={['bottom']}
					type={PopoverType.background}
					className={css(mobileStyles.whereToSharePopover)}
				>
					<div className={css(mobileStyles.whereToShareDropdownContainer)}>
						{availableConnections.map(connection => renderConnectionItem(connection))}
					</div>
				</TinyPopover>
			</div>
		);
	}
);

export { MobileWhereToShare };
